import { Injectable } from '@angular/core';
import { AlbertaDebugConfigService } from '@services/alberta-debug/alberta-debug-config.service';
import { Observable, Subject } from 'rxjs';
import { ConfigDB } from 'src/app/common/repository/databases';
import type { ConfigDto } from 'src/app/shared/models/config/config-dto.model';
import { QueryService } from 'src/app/shared/services/query/query.service';

import {
  ICareboxConfig,
  IConfig,
  IDeactivationReasonConfig,
  IDeliveryInformationConfig,
  IImageManipulatorConfig,
  IKnowledgeBaseConfig,
  IListConfig,
  IPatientDataConfig,
  IPatientStatus,
  IPostalCodeInfoConfig,
  IServiceCostEstimate,
  IShippingInformation,
  ISpecializedServiceProviderBranch,
  IStorageLocation,
} from '@alberta/konexi-shared';
import { IKiwiConfig } from '@alberta/konexi-shared/dist/general/config/kiwi-config';
import { DocumentSaveInteractionType } from '@alberta/konexi-shared/dist/general/enum/document-save-interaction-type';
import type { IRoleTranslation } from './caregiver-role-translation';

@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private _config: ConfigDto; // merged config (base + overrides)
  private _baseConfig: ConfigDto; // raw config from backend/DB
  private _configUpdated$ = new Subject<IConfig>();
  /** Emits whenever the tenant config changes (e.g. when it arrives/updates via sync). */
  public readonly configUpdated$: Observable<IConfig> = this._configUpdated$.asObservable();

  constructor(
    private _queryService: QueryService,
    private _debug: AlbertaDebugConfigService
  ) {}

  public async getTenantConfig(): Promise<IConfig | null> {
    if (!this._baseConfig) {
      const config = await this._queryService.getAll<ConfigDto>(ConfigDB);
      if (config?.[0]) {
        this._baseConfig = config[0];
      }
    }
    this._config = this._applyDebugOverridesIfDev(this._baseConfig) as ConfigDto;
    return this._config;
  }

  public updateTenantConfig(config: IConfig): void {
    if (!config) {
      return;
    }
    this._baseConfig = config as ConfigDto;
    this._config = this._applyDebugOverridesIfDev(this._baseConfig) as ConfigDto;
    this._configUpdated$.next(this._config);
  }

  private _applyDebugOverridesIfDev(base: ConfigDto | undefined): ConfigDto | undefined {
    if (!base) return base;
    if (this._debug.isDevEnvironment()) {
      const merged = this._debug.applyOverrides(base) as ConfigDto | null;
      return (merged ?? base) as ConfigDto;
    }
    return base;
  }

  public async showResmedTenant(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showResmedTenant);
  }

  public async showCooperationPartner(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showCooperationPartner);
  }
  public async getWoundScales(): Promise<number[]> {
    const config = await this.getTenantConfig();
    return config?.woundScaleConfig?.woundScalesInMillimeter;
  }

  public async showDevices(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showDevices);
  }

  public async showIntegratedCare(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showIntegratedCare);
  }

  public async showCareProposal(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showCareProposal);
  }

  public async specializedServiceProviderEnabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.specializedServiceProviderConfig?.enabled);
  }

  public async showLimitedReport(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.document?.disableReports);
  }

  public async getPatientDataConfig(): Promise<IPatientDataConfig | undefined> {
    const config = await this.getTenantConfig();
    return await config?.patientData;
  }

  public async getDeactivationReasonConfig(): Promise<IDeactivationReasonConfig[] | undefined> {
    const config = await this.getTenantConfig();
    return await config?.patientData?.deactivationReasonConfig;
  }

  public async hashAudits(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.document?.hashAudit);
  }

  public async saveImagesAtAudit(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return !config?.document?.dontSaveImagesAtAudit;
  }

  public async completeAuditsOnCreate(): Promise<DocumentSaveInteractionType> {
    const config = await this.getTenantConfig();
    if (config?.document?.completeOnCreate == null || typeof config?.document?.completeOnCreate === 'boolean') {
      return config?.document?.completeOnCreate
        ? DocumentSaveInteractionType.PromptForCompleteOrSave
        : DocumentSaveInteractionType.NoPrompt;
    }
    return config?.document?.completeOnCreate;
  }

  public async getDeliveryInformation(): Promise<IDeliveryInformationConfig[]> {
    const config = await this.getTenantConfig();
    return config?.deliveryInformation;
  }

  public async showIntegratedCareAbo(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showAbo);
  }

  public async showRegionFilter(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showRegionFilter);
  }

  public async showStatusFilter(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showStatusFilter);
  }

  public async usedirectOrder(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.directOrder);
  }
  public async canEditInsuranceContract(): Promise<boolean> {
    const config = await this.getTenantConfig();

    return !!config?.insuranceContract?.canEdit;
  }
  public async calculateContractOnStandardCareProposal(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.contractCalculationOnStandardCareProposal);
  }

  public async canCalculateByContractArticleGroup(): Promise<boolean> {
    const config = await this.getTenantConfig();

    return !!config?.integratedCare?.contractArticleGroupCalculation;
  }

  public async canAddAddtionalFieldNurse(): Promise<boolean> {
    const config = await this.getTenantConfig();

    return !!config?.patient?.canAddAdditionalFieldNurse;
  }

  public async showHospitalPrescriberToggle(): Promise<boolean> {
    const config = await this.getTenantConfig();

    return !!config?.orderConfig?.showOrderPrescriberToggle;
  }

  public async showSendOrderDialog(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.showSendOrderDialog ?? true;
  }

  public async showCrossTherapyArticleSearch(): Promise<boolean> {
    const config = await this.getTenantConfig();

    return Boolean(config?.integratedCare?.showCrossTherapyArticleSearch);
  }

  public async getClassificationLabel(): Promise<string> {
    const config = await this.getTenantConfig();
    return config?.classificationLabel;
  }

  public async classificationRequired(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.classificationRequired);
  }

  public async deliveryInformationRequired(): Promise<boolean> {
    const config = await this.getTenantConfig();

    return Boolean(config?.deliveryInformationRequired);
  }

  public async getPatientClassifications(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.patientClassifications;
  }

  public async getOrderMaxAnnotationLength(): Promise<number> {
    const config = await this.getTenantConfig();
    return config?.orderConfig.maxAnnotationLength;
  }

  public async getShippingInformation(): Promise<IShippingInformation[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.shippingInformation;
  }
  public async showPatientDeliveryInformation(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return config?.patientData?.showDeliveryInformation;
  }
  public async showCalorieDemand(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return config?.patientData?.showCalorieDemand;
  }

  public async hideBuildNumber(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.hideBuildNumber);
  }
  public async mustSetDeliveryDate(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return !config?.orderConfig?.deliveryDateEmpty;
  }
  public async canSetDeliveryDateUnknown(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.deliveryDateUnknown);
  }
  public async getStorageLocations(): Promise<IStorageLocation[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.storageLocation;
  }
  public async showIntegratedCareStartDate(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showStartDate);
  }

  public async keepShoppingCart(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.keepShoppingCart);
  }

  public async alwaysShowStandardCareProposalTab(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.alwaysShowStandardCareProposalTab);
  }

  public async showNativeShareButton(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.share?.shareAttachments);
  }

  public async showNativeUrlShareButton(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.share?.shareUrls);
  }

  public async getBrowserShareTarget(): Promise<string> {
    const config = await this.getTenantConfig();
    return config?.share?.browserShareTarget;
  }

  public async showNotAutomaticDelivery(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showNotAutomaticDelivery);
  }

  public async showFollowUpCareToggle(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showFollowUpCareToggle);
  }

  public async transmitIntegratedCare(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.transmitIntegratedCare);
  }

  public async canAddPrescriptionRequest(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.prescription?.canAddPrescriptionRequest);
  }

  public async canQueryStock(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showStock);
  }

  public async showDiagnose(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showDiagnose);
  }

  public async showStomaCreationDate(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.showStomaCreationDate);
  }

  public async useAdvancedReturnDeliveryProcess(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.returnDeliveryConfig?.useAdvancedProcess);
  }

  public async shouldShowMonthlyOrder(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.showMonthlyOrder);
  }

  /**
   * Always send
   * (see documentation: "Abschicken von Versorgungen")
   */
  public async isCustomerGroupAlwaysSend(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.transmitIntegratedCare);
  }

  /**
   * Never send
   * (see documentation: "Abschicken von Versorgungen")
   */
  public async isCustomerGroupNeverSend(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return !config?.integratedCare?.transmitIntegratedCare && !config?.integratedCare?.showAbo;
  }

  /**
   * Send if abo changed
   * (see documentation: "Abschicken von Versorgungen")
   */
  public async isCustomerGroupSendOnAboChange(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return !config?.integratedCare?.transmitIntegratedCare && Boolean(config?.integratedCare?.showAbo);
  }

  // get role-translation from config DB
  public async getRoleTranslation(): Promise<IRoleTranslation> {
    const config = await this.getTenantConfig();
    const rolesFromConfig = config.caregiverRoles;
    const defaultRoleTranslation: IRoleTranslation = {
      legal_carer: {
        de: 'Gesetzlicher Betreuer',
      },
      intermediary: {
        de: 'Vermittler',
      },
      application_consultant: {
        de: 'Anwendungsberater',
      },
      assigner: {
        de: 'Zuweiser',
      },
      phone_contact: {
        de: 'Telefonkontakt',
      },
      billing_contact: {
        de: 'Rechnungskontakt',
      },
      health_care_representative: {
        de: 'Vorsorgebevollmächtigter',
      },
      invoice_recipient: {
        de: 'Rechnungsempfänger',
      },
    };
    // return default role if not set
    if (rolesFromConfig === undefined || rolesFromConfig === null) {
      return defaultRoleTranslation;
    }
    if (rolesFromConfig.length === 0) {
      return defaultRoleTranslation;
    }
    const roleTranslations: IRoleTranslation = {};
    rolesFromConfig.forEach(role => {
      // set default for de if not set
      if (role.translation === undefined) {
        roleTranslations[role.key] = {
          de: role.key,
        };
      } else {
        roleTranslations[role.key] = role.translation;
      }
    });
    return roleTranslations;
  }

  public async showKnowledgeBase(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showKnowledgeBase);
  }

  public async getKnowledgeBaseConfig(): Promise<IKnowledgeBaseConfig> {
    const config = await this.getTenantConfig();
    return config?.knowledgeBaseConfig;
  }

  public async hasDeliveryNote(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.hasDeliveryNote);
  }

  public async hasPatientDeliveryNote(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.patientData?.hasDeliveryNote);
  }

  public async getDeliveryNoteMaxLength(): Promise<number> {
    const config = await this.getTenantConfig();
    if (config.orderConfig?.deliveryNoteMaxLength) {
      return config.orderConfig.deliveryNoteMaxLength;
    } else {
      return 128;
    }
  }
  public async isIntegratedCareArchiveAllowed(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.allowArchive);
  }

  public async getPatientStatuses(): Promise<IPatientStatus[] | undefined> {
    const config = await this.getTenantConfig();
    return config?.patientData?.statuses;
  }

  public async getIntegratedCareMaxAnnotationLength(): Promise<number> {
    const config = await this.getTenantConfig();
    return config?.integratedCare?.maxAnnotationLength;
  }

  public async showRequiredDocumentsBasedOnStandardCareProposal(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showRequiredDocumentsBasedOnStandardCareProposal);
  }

  public async getPostalCodeInfo(): Promise<IPostalCodeInfoConfig[]> {
    const config = await this.getTenantConfig();
    return config?.postalCodeInfo;
  }

  public async getPatientAttributeConfig(): Promise<IConfig['patientData']['attributeConfig']> {
    const config = await this.getTenantConfig();
    return config?.patientData?.attributeConfig || [];
  }

  public async canEditDoctor(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.canEditDoctor);
  }

  public async canSendAlbertaConnectDocuments(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.albertaConnect?.sendDocument);
  }

  public async canSaveDocumentAsDraft(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.document?.saveAsDraft);
  }

  public async getPrescriptionRequestAttachmentTypes() {
    const config = await this.getTenantConfig();
    return config?.prescription?.attachmentTypes;
  }

  public async hideGeneralContracts(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.integratedCare?.hideGeneralContracts);
  }

  public async getOrderListConfig(): Promise<IListConfig> {
    const config = await this.getTenantConfig();
    return config?.orderListConfig;
  }

  public async getDocumentListConfig(): Promise<IListConfig> {
    const config = await this.getTenantConfig();
    return config?.documentListConfig;
  }

  public async getServiceTaskListConfig(): Promise<IListConfig> {
    const config = await this.getTenantConfig();
    return config?.serviceTaskListConfig;
  }

  public async getCareboxConfig(): Promise<ICareboxConfig | null> {
    const config = await this.getTenantConfig();
    return config?.carebox;
  }

  public async displayMedicalAidArticleNameOnPrescription(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.prescription?.displayMedicalAidArticleName);
  }

  public async canAddMedicalOrder(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.medicalOrder?.canAddMedicalOrder);
  }

  public async kimEnabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.kimConfig?.enabled);
  }

  public async serviceCostEstimateCorrespondenceTypes(): Promise<IServiceCostEstimate['correspondenceType'][]> {
    const config = await this.getTenantConfig();
    return config?.serviceCostEstimate?.correspondenceTypes || ['Fax'];
  }

  public async showErpOrder(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.showErpOrder);
  }

  public async showCalendar(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.calendarConfig?.enabled);
  }

  public async showPatientDataCalendarResources(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.patientData?.showCalendarResources);
  }

  public async showAppointmentTypeWoundcenter(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.appointmentConfig?.showWoundcenter);
  }

  public async showResourceForAppointment(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.appointmentConfig?.showResource);
  }

  public async videoChatEnabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.videoChat?.enabled);
  }

  public async getOrderTags(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.orderTags?.values;
  }

  public async orderTagsSelectStyle(): Promise<'single' | 'multiple'> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.orderTags?.selectStyle || 'multiple';
  }

  public async orderTagsRequired(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.orderTags?.required ?? false;
  }

  public async useAOKTagPrefillWorkflow(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.useAOKTagPrefillWorkflow);
  }

  public async groupOrder(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.groupOrder);
  }

  public async shouldShowReferrer(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.patientData?.showReferrer);
  }

  public async referrerRequired(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.patientData?.showReferrer && config?.required?.referrer);
  }

  public async getAdditionalReferrers(): Promise<{ id: number; name: string }[]> {
    const config = await this.getTenantConfig();
    return config?.patientData?.additionalReferrers;
  }

  public async specializedServiceProviderBranches(): Promise<ISpecializedServiceProviderBranch[]> {
    const config = await this.getTenantConfig();
    return config?.specializedServiceProviderConfig?.branches || [];
  }

  public async forbidCreationWithPatientStatus(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.forbidCreationWithPatientStatus || [];
  }

  public async onlyAllowSendingWithPrescriptionStatus(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.onlyAllowSendingWithPrescriptionStatus || [];
  }

  public async requireRelevantAttachmentForPrescriptionStatus(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.requireRelevantAttachmentForPrescriptionStatus);
  }

  public async autoSetPrescriptionStatusOnAttachmentUpload(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.autoSetPrescriptionStatusOnAttachmentUpload);
  }

  public async onlyAllowPrescriptionRequestWithOrderStatus(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.onlyAllowPrescriptionRequestWithOrderStatus || [];
  }

  public async onlyAllowAttachmentUploadWithOrderStatus(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.orderConfig?.onlyAllowAttachmentUploadWithOrderStatus || [];
  }

  public async lockscreenEnabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.lockscreenConfig?.enabled) || false;
  }

  public async lockScreenAfterMillisecondsInBackground(): Promise<number> {
    const config = await this.getTenantConfig();
    return config?.lockscreenConfig?.lockScreenAfterMillisecondsInBackground;
  }

  public async shouldHideContentsInBackground(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.lockscreenConfig?.shouldHideContentInBackground);
  }

  public async disableUnknownPayer(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.disableUnknownPayer);
  }

  public async allowManualTaskCreation(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.specializedServiceProviderConfig?.allowManualTaskCreation);
  }

  public async getKiwiConfig(): Promise<IKiwiConfig | null> {
    const config = await this.getTenantConfig();
    return config?.kiwiConfig;
  }
  public async getKiwiAttachmentTabName(): Promise<string> {
    const config = await this.getTenantConfig();
    return config?.kiwiConfig?.attachmentTabName;
  }

  public async singleOrderDocumentsEnabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.enableSingleOrderDocuments);
  }

  public async woundLocationHealedFlagOnAuditEnabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.document?.enableWoundLocationHealedFlag);
  }

  public async isNumberOfInsuredValidatorDisabled(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.patient?.disableInsuranceNumberValidation);
  }

  public async getDeliveryOptions(): Promise<string[]> {
    const config = await this.getTenantConfig();
    return config?.deliveryOptions;
  }
  public async getImageManipulatorConfig(): Promise<IImageManipulatorConfig> {
    const config = await this.getTenantConfig();
    return config?.imageManipulatorConfig || { quality: 80, maxHeight: 1500, maxWidth: 1000 };
  }

  public async onlyAllowSendingWithTransmittedIntegratedCare(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.orderConfig?.onlyAllowSendingWithTransmittedIntegratedCare);
  }

  public async showIcd10(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.patientData?.medicalData?.showIcd10);
  }

  public async hideTicketButton(): Promise<boolean> {
    const config = await this.getTenantConfig();
    return Boolean(config?.dashboardConfig?.hideTicketButton);
  }
}
